import type {
  AcademicTerm,
  AssignmentCopyRequest,
  AssignmentFolderAction,
  DailyMemoAction,
  DailyRecordAction,
  DrawAction,
  HomeroomAffairChange,
  OperationV2,
  RosterRow,
  SchoolCalendarChange,
  SchoolYearAction,
  SyncClock,
} from "@classroom/domain";
import type { ClassSetupDraft } from "../classSetup";
import type { LocalAccount } from "./accountScope";
import type { ClassSettingsUpdate, LocalSnapshot, StoredRecord } from "./repository";
import type { Collection } from "./schema";
import type { LocalSyncState, SyncAttachment } from "./syncV2Store";

export type LocalOperation =
  | {
      kind: "saveHomeroomAffairChange";
      workspaceId: string;
      classId: string;
      date: string;
      change: HomeroomAffairChange;
    }
  | { kind: "copyTeachingAssignments"; workspaceId: string; request: AssignmentCopyRequest }
  | { kind: "backupProgress"; workspaceId: string }
  | { kind: "captureAutomaticBackup"; workspaceId: string }
  | {
      kind: "acknowledgeAutomaticBackup";
      workspaceId: string;
      revision: number | null;
      now: number;
      restoreId?: string;
    }
  | {
      kind: "restoreAutomaticBackup";
      workspaceId: string;
      snapshot: LocalSnapshot;
      expectedHash: string;
      restoreId?: string;
    }
  | { kind: "syncV2Maintenance"; workspaceId: string; value: "drive-rebind" | null }
  | { kind: "syncV2Resume"; workspaceId: string }
  | { kind: "syncV2RemoteGeneration"; workspaceId: string; expected: SyncAttachment }
  | { kind: "syncV2ImportLegacy"; workspaceId: string; snapshot: LocalSnapshot }
  | { kind: "syncV2VerifyJoinBackup"; workspaceId: string; snapshot: LocalSnapshot }
  | { kind: "syncV2Join"; workspaceId: string; scope: SyncAttachment; key: CryptoKey; receipt?: string }
  | { kind: "syncV2Rekey"; workspaceId: string; scope: SyncAttachment; key: CryptoKey }
  | { kind: "syncV2Generation"; workspaceId: string; scope: SyncAttachment; key: CryptoKey; receipt: string }
  | { kind: "syncV2Checkpoint"; workspaceId: string; command: import("./syncCheckpointStore").CheckpointCommand }
  | { kind: "syncV2Stage"; workspaceId: string; operations: OperationV2[] }
  | { kind: "syncV2Drain"; workspaceId: string; final: boolean }
  | { kind: "syncV2Seed"; workspaceId: string }
  | { kind: "syncV2CreateLocal"; workspaceId: string; scope: SyncAttachment; key: CryptoKey }
  | { kind: "syncV2Stats"; workspaceId: string }
  | {
      kind: "syncV2Resolve";
      workspaceId: string;
      decision: import("@classroom/domain").ConflictV2;
      operationId: string;
    }
  | { kind: "syncV2ConflictLabel"; workspaceId: string; key: string }
  | { kind: "syncV2Disconnect"; workspaceId: string }
  | { kind: "syncV2State"; workspaceId: string }
  | { kind: "syncV2Attach"; workspaceId: string; scope: SyncAttachment }
  | { kind: "syncV2Unlock"; workspaceId: string; scope: SyncAttachment; key: CryptoKey }
  | {
      kind: "syncV2Online";
      workspaceId: string;
      expiresAt: number;
      expected: Pick<SyncAttachment, "datasetId" | "generation">;
    }
  | { kind: "syncV2Deny"; workspaceId: string }
  | { kind: "syncV2Pending"; workspaceId: string }
  | { kind: "syncV2Operation"; workspaceId: string; operationId: string }
  | { kind: "syncV2Allocate"; workspaceId: string; operationId: string; fileId: string }
  | { kind: "syncV2Acknowledge"; workspaceId: string; operation: OperationV2; fileId: string }
  | { kind: "syncV2Receive"; workspaceId: string; operations: OperationV2[] }
  | {
      kind: "syncV2Cursor";
      workspaceId: string;
      cursor: string;
      expected: Pick<SyncAttachment, "datasetId" | "generation">;
    }
  | { kind: "syncV2Conflicts"; workspaceId: string }
  | { kind: "accountStatus" }
  | { kind: "claimLegacyWorkspace"; email: string }
  | { kind: "deferLegacyWorkspace" }
  | { kind: "previewCloudImport"; workspaceId: string; text: string }
  | { kind: "importCloudExport"; workspaceId: string; text: string; checksum: string; targetChecksum: string }
  | { kind: "ensureWorkspace"; workspace: StoredRecord; hiddenTools?: string[] }
  | { kind: "saveClassSetup"; workspaceId: string; draft: ClassSetupDraft; yearId?: string }
  | { kind: "importStudents"; workspaceId: string; classId: string; rows: RosterRow[] }
  | { kind: "addStudent"; workspaceId: string; classId: string; row: RosterRow }
  | { kind: "saveAcademicTerms"; workspaceId: string; terms: AcademicTerm[]; yearId?: string }
  | { kind: "loadYear"; workspaceId: string; yearId?: string; date?: string }
  | { kind: "ensureDateSchoolYear"; workspaceId: string; date: string }
  | { kind: "saveSchoolYearAction"; workspaceId: string; action: SchoolYearAction }
  | { kind: "previewSchoolYearDeletion"; workspaceId: string; yearId: string }
  | { kind: "saveWorkspace"; workspace: StoredRecord }
  | { kind: "saveHiddenTools"; workspaceId: string; hiddenTools: string[] }
  | { kind: "updateClassSettings"; workspaceId: string; classId: string; update: ClassSettingsUpdate }
  | { kind: "saveDrawAction"; workspaceId: string; classId: string; action: DrawAction }
  | { kind: "saveAssignmentFolderAction"; workspaceId: string; classId: string; action: AssignmentFolderAction }
  | { kind: "saveDailyMemoAction"; workspaceId: string; classId: string; action: DailyMemoAction }
  | { kind: "saveDailyRecordAction"; workspaceId: string; classId: string; date: string; action: DailyRecordAction }
  | { kind: "load"; workspaceId: string }
  | { kind: "exportBackupSource"; workspaceId: string }
  | { kind: "validateSnapshot"; workspaceId: string; snapshot: LocalSnapshot }
  | {
      kind: "validateBackupImage";
      workspaceId: string;
      snapshot: LocalSnapshot;
      sqliteImage: string;
      schemaVersion: number;
    }
  | { kind: "get"; workspaceId: string; collection: Collection; recordId: string }
  | { kind: "put"; workspaceId: string; collection: Collection; record: StoredRecord }
  | { kind: "putMany"; workspaceId: string; collection: Collection; records: StoredRecord[] }
  | { kind: "putCollections"; workspaceId: string; batches: Array<{ collection: Collection; records: StoredRecord[] }> }
  | { kind: "saveSchoolCalendarChanges"; workspaceId: string; changes: SchoolCalendarChange[] }
  | { kind: "remove"; workspaceId: string; collection: Collection; recordId: string }
  | { kind: "removeMany"; workspaceId: string; collection: Collection; recordIds: string[] }
  | { kind: "counts"; workspaceId: string }
  | { kind: "deleteEmptyClass"; workspaceId: string; classId: string; confirmedName: string }
  | { kind: "deleteStudentData"; workspaceId: string; studentId: string }
  | {
      kind: "deleteTeachingAssignment";
      workspaceId: string;
      classId: string;
      assignmentId: string;
      confirmationToken: string;
    }
  | { kind: "replaceSnapshot"; workspaceId: string; snapshot: LocalSnapshot };

export type LocalRequest = {
  dataEpoch?: string;
  id: number;
  operation: LocalOperation;
  account?: LocalAccount;
  accountEpoch?: number;
  reviewId?: string;
  observedSyncClock?: SyncClock;
};
// Exhaustive: adding an operation requires choosing its write capability.
export const localOperationAccess: Record<LocalOperation["kind"], "read" | "classroom" | "control" | "restore"> = {
  backupProgress: "read",
  captureAutomaticBackup: "read",
  acknowledgeAutomaticBackup: "control",
  restoreAutomaticBackup: "restore",
  syncV2CreateLocal: "control",
  syncV2Resolve: "control",
  syncV2Checkpoint: "control",
  syncV2Join: "control",
  syncV2Rekey: "control",
  syncV2Generation: "control",
  syncV2ImportLegacy: "control",
  syncV2Maintenance: "control",
  syncV2Resume: "control",
  syncV2RemoteGeneration: "control",
  syncV2VerifyJoinBackup: "control",
  syncV2Stage: "control",
  syncV2Drain: "control",
  syncV2ConflictLabel: "control",
  accountStatus: "read",
  claimLegacyWorkspace: "classroom",
  deferLegacyWorkspace: "read",
  ensureWorkspace: "read",
  load: "read",
  loadYear: "read",
  get: "read",
  counts: "read",
  exportBackupSource: "read",
  validateSnapshot: "read",
  validateBackupImage: "read",
  previewCloudImport: "read",
  previewSchoolYearDeletion: "read",
  importCloudExport: "restore",
  saveClassSetup: "classroom",
  addStudent: "classroom",
  importStudents: "classroom",
  saveAcademicTerms: "classroom",
  ensureDateSchoolYear: "classroom",
  saveSchoolYearAction: "classroom",
  saveWorkspace: "classroom",
  saveHiddenTools: "classroom",
  updateClassSettings: "classroom",
  saveDrawAction: "classroom",
  saveAssignmentFolderAction: "classroom",
  saveDailyMemoAction: "classroom",
  saveHomeroomAffairChange: "classroom",
  copyTeachingAssignments: "classroom",
  saveDailyRecordAction: "classroom",
  put: "classroom",
  putMany: "classroom",
  putCollections: "classroom",
  saveSchoolCalendarChanges: "classroom",
  remove: "classroom",
  removeMany: "classroom",
  deleteEmptyClass: "classroom",
  deleteStudentData: "classroom",
  deleteTeachingAssignment: "classroom",
  replaceSnapshot: "restore",
  syncV2State: "control",
  syncV2Attach: "control",
  syncV2Unlock: "control",
  syncV2Online: "control",
  syncV2Deny: "control",
  syncV2Pending: "control",
  syncV2Operation: "control",
  syncV2Allocate: "control",
  syncV2Acknowledge: "control",
  syncV2Receive: "control",
  syncV2Cursor: "control",
  syncV2Conflicts: "control",
  syncV2Seed: "control",
  syncV2Stats: "control",
  syncV2Disconnect: "control",
};
export type LocalResponse =
  | { id: number; ok: true; result: unknown; syncState?: LocalSyncState | null; dataEpoch?: string }
  | { id: number; ok: false; error: string };
